import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { CourseMaterial, useProgramDetails } from "./coursesLoader";
import { parseDate } from "common/utils/time";
import useMaterialOptDeadline from "./useMaterialOptDeadline";
import useFindCourse from "./useFindCourse";
import useOptedIn from "./useOptedIn";

export default function MaterialStartDate({
  material,
  className,
}: {
  material: CourseMaterial;
  className?: string;
}) {
  const {
    scope,
    userEffectiveProgram,
    ea: { allowsOptOut },
  } = useProgramDetails();
  const optedIn = useOptedIn(material);
  const { optWindowOpen } = useMaterialOptDeadline(material);
  const isInstructor =
    useFindCourse({ courseId: material.courseId })?.userRole === "instructor";
  const { trialStartDate } = material;
  const startDate = trialStartDate ? parseDate(trialStartDate) : null;

  const isInvalidMaterial = useMemo(() => {
    if (
      material.materialFormat !== "etextbook" ||
      material.verbaLicense?.pending ||
      !material.materialStatus ||
      material.materialStatus === "included" ||
      material.materialStatus === "not_included" ||
      !optWindowOpen ||
      (isInstructor && material.businessModels === "ePOS")
    ) {
      return true;
    }
    return false;
  }, [material, isInstructor, optWindowOpen]);

  const hasInvalidOrPastStartDate = useMemo(
    () => !startDate || new Date() > startDate,
    [startDate]
  );

  const isOptedOutOfProgram = useMemo(() => {
    const eaOptedOut = !allowsOptOut || !optedIn;
    const iaOptedOut = !optedIn;

    if (scope === "flexible") {
      if (
        userEffectiveProgram === "no_program" ||
        userEffectiveProgram === "inclusive_access"
      ) {
        return iaOptedOut;
      } else if (userEffectiveProgram === "equitable_access") {
        return eaOptedOut;
      }
    } else if (scope === "ea_only") {
      return eaOptedOut;
    } else if (scope === "ia_only") {
      return iaOptedOut;
    }
    return true;
  }, [allowsOptOut, optedIn, scope, userEffectiveProgram]);

  if (isInvalidMaterial || hasInvalidOrPastStartDate || isOptedOutOfProgram) {
    return null;
  }

  return (
    <div className={className}>
      <FormattedMessage
        defaultMessage={
          "Item will be available starting on <strong>{startDate, date, long}</strong>"
        }
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
          startDate,
        }}
      />
    </div>
  );
}
